import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Car, FileSearch, Send, Banknote, ArrowRight, CheckCircle } from 'lucide-react';

const HowItWorksSection = () => {
  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-background">
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
            Simple 3-Step Process
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            How It Works
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Claiming back your car finance commission is straightforward. 
            Our streamlined process makes it easy to get started.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 mb-16">
          {/* Step 1 */}
          <div className="relative group">
            <Card className="elevated-card h-full border-border/60">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent-glow flex items-center justify-center shadow-accent">
                      <Car className="w-7 h-7 text-accent-foreground" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gold text-gold-foreground text-xs font-bold flex items-center justify-center shadow-gold">
                      01
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold text-foreground">
                      Enter Your Details
                    </h3>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Simply enter your vehicle registration number and basic details. It takes a few minutes to start your claim.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Quick online form</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">No finance paperwork needed</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Simple process</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Step 2 */}
          <div className="relative group">
            <Card className="elevated-card h-full border-border/60">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent-glow flex items-center justify-center shadow-accent">
                      <FileSearch className="w-7 h-7 text-accent-foreground" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gold text-gold-foreground text-xs font-bold flex items-center justify-center shadow-gold">
                      02
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold text-foreground">
                      We Assess Your Claim
                    </h3>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  We analyse the terms and determine the strength of your claim against each lender.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Expert legal analysis</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Full agreement review</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Identify hidden fees</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Step 3 */}
          <div className="relative group">
            <Card className="elevated-card h-full border-border/60">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent-glow flex items-center justify-center shadow-accent">
                      <Send className="w-7 h-7 text-accent-foreground" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gold text-gold-foreground text-xs font-bold flex items-center justify-center shadow-gold">
                      03
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold text-foreground">
                      We Submit Your Claim
                    </h3>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  We handle all correspondence with lenders and can escalate your case to the Financial Ombudsman Service or issue court proceedings if necessary.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Formal complaint submission</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Ombudsman escalation if needed</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Court proceedings available</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Step 4 */}
          <div className="relative group">
            <Card className="elevated-card h-full border-border/60">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent-glow flex items-center justify-center shadow-accent">
                      <Banknote className="w-7 h-7 text-accent-foreground" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gold text-gold-foreground text-xs font-bold flex items-center justify-center shadow-gold">
                      04
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold text-foreground">
                      Receive Your Compensation
                    </h3>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Compensation amounts vary based on individual circumstances.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">No win, no fee</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Paid to your solicitor's client account</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    <span className="text-foreground/80">Case-by-case assessment</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button variant="gold" size="xl" asChild>
            <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
              Find Your Agreements
              <ArrowRight className="w-5 h-5" />
            </a>
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            No win, no fee
          </p>
        </div>
      </div>
    </section>
  );
};

export { HowItWorksSection };
